import { ComplexPolygon } from '../../../extensions/CompGeom/complex-polygon';
import { BBOX_SIZE, FRAG_SIZE, serializeFragment, serializeBoundingBox, base64EncArr } from '../../wgs/scene/FragmentData';
import { ElementFlags, QC } from '../schema/dt-schema';
import { DtConstants } from '../schema/DtConstants';
import { FacetLevels } from '../../dt/facets/Facets';
import { FRAGMENT_LOADED_EVENT } from '../../application/EventTypes';

function fuzzyEqual(a, b, eps = 1e-4) {
  return Math.abs(b - a) < eps;
}

export const Operation = Object.freeze({
  CREATING: "Creating",
  EDITING: "Editing"
});

const SPACE_HEIGHT_NON_METRIC = 8.2021;
const SPACE_HEIGHT_METRIC = 2.5;

function getDefaultSpaceHeight(shape) {
  const dUnit = shape.customData.model.getMetadata('distance unit');
  if (dUnit === undefined || dUnit.value === 'foot') {
    return SPACE_HEIGHT_NON_METRIC;
  }

  return SPACE_HEIGHT_METRIC;
}

function getLevelHeight(facility, shape, planeHeight) {
  const level = shape.customData.level;
  if (!level) {
    // allow sketching on surfaces without level information, assign default height
    return getDefaultSpaceHeight(shape);
  }

  let levels = Object.values(shape.customData.model.getLevels());
  levels.sort(FacetLevels.sortByElev);

  // first, try to compute level's height based on the next level elevation
  const levelIdx = levels.findIndex((l) => l.name === level.originalName);
  if (levelIdx !== -1) {
    const nextLevel = levels[levelIdx + 1];
    if (nextLevel !== undefined) {
      return nextLevel.elev - planeHeight;
    }
  }

  // use computed height
  const floorCache = facility.facetsManager.facetsEffects._floorHighlightCache;
  const levelBBox = floorCache[level.name]?.mesh.geometry.boundingBox;
  if (levelBBox) {
    const min = levelBBox.min;
    const max = levelBBox.max;
    const height = Math.abs(max.z - min.z);
    return height;
  }

  return getDefaultSpaceHeight(shape);
}

export async function createOrUpdateSpace(facility, viewer, layer, shape) {
  let vertices = shape.points.map((v) => v);

  if (!shape.isCCW()) {
    vertices = vertices.reverse();
  }

  let indices = shape.points.map((v, i) => i);
  indices.push(0); // close polygon

  let polygon = ComplexPolygon.createNewPolygon(vertices, null, shape.getBBox());
  polygon.addContour(indices);
  polygon.triangulate();

  if (polygon.triangulationFailed) {
    throw new Error("Failed to triangulate sketched geometry");
  }

  let z = layer.getPlaneHeight();
  if (fuzzyEqual(z, 0.0, 0.01)) {
    // we failed to compute any intersection with underlying geometry, use level's elevation
    z = shape.cdata?.level?.elev || 0.0;
  }
  console.log('Room elevation: ', z);

  let height = shape.customData.existingHeight ?? getLevelHeight(facility, shape, z);
  console.log('Extrude height: ', height);

  if (fuzzyEqual(height, 0.0, 0.01)) {
    console.log('Fixed zero height and set it to 1.0');
    height = 1.0;
  }

  const geom = polygon.toExtrudedMesh(height);
  if (geom) {
    let defaultModel = facility.getDefaultModel();
    // 'toExtrudedMesh()' above extrudes from maxZ=0 to minZ=-thickness
    // so here we add z value we got on mouse hit plus level height
    let res = await defaultModel.createGeometry(geom);
    res.transform[14] += z + height;

    const fragmentData = serializeFragment(0, res.hash, null, res.transform, true);
    const lmvFragment = base64EncArr(fragmentData, 0, FRAG_SIZE);

    let bbox = geom.boundingBox;
    bbox.min.z = z;
    bbox.max.z = z + height;

    const boundingBoxData = serializeBoundingBox(bbox);
    const lmvBBox = base64EncArr(boundingBoxData, 0, BBOX_SIZE);

    const svg = shape.toSVG();

    let elems = [];

    if (shape.customData.op === Operation.CREATING) {
      let elemData = {
        [QC.Name]: "New room",
        [QC.ElementFlags]: ElementFlags.Room,
        [QC.TandemCategory]: DtConstants.TC.Rooms,
        [QC.LmvFragment]: lmvFragment,
        [QC.LmvBoundingBox]: lmvBBox,
        [QC.Centerline]: svg,
        [QC.Elevation]: z,
        [QC.Height]: height
      };

      if (shape.customData.level) {
        await facility.addLevelFromElement(elemData, shape.customData.model, shape.customData.level.dbId);
      }

      elems = await defaultModel.createElementsWithData([elemData], DtConstants.ChangeTypes.CreateSpace);
    } else {
      const elemData = {
        [QC.LmvDbId]: shape.customData.dbId,
        [QC.LmvFragment]: lmvFragment,
        [QC.LmvBoundingBox]: lmvBBox,
        [QC.Centerline]: svg,
        [QC.Elevation]: z,
        [QC.Height]: height
      };

      const rooms = defaultModel.getRooms();
      const room = rooms[shape.customData.dbId];
      if (room) {
        elems.push(room.externalId);
      }

      await defaultModel.updateElementsWithData([elemData], DtConstants.ChangeTypes.UpdateSpace);
    }

    facility.hideModel(defaultModel);
    facility.unloadModel(defaultModel, true);
    facility.loadModel(defaultModel);

    if (shape.customData.op === Operation.CREATING) {
      const cb = ({ model, svf, fragId }) => {
        if (!model.isDefault()) {
          return;
        }

        const dbId = svf.fragments.fragId2dbId[fragId];
        if (dbId === undefined) {
          return;
        }

        const newDbId = svf.autoDbIds.getDbIdFromB64ExternalId(elems[0]);
        if (newDbId !== dbId) {
          return;
        }

        viewer.select([dbId], model);

        viewer.removeEventListener(FRAGMENT_LOADED_EVENT, cb);
      };

      viewer.addEventListener(FRAGMENT_LOADED_EVENT, cb);
    }

    return elems;
  }

  return [];
}

export async function updateSpaceHeightOrElevation(facility, dbId, centerline, elevation, height, correlationId, heightChanged) {
  const shape = Autodesk.Edit2D.Shape.fromSVG(centerline);

  // we are not changing any vertices, shape is CCW oriented
  let vertices = shape.points.map((v) => v);

  let indices = shape.points.map((v, i) => i);
  indices.push(0); // close polygon

  let polygon = ComplexPolygon.createNewPolygon(vertices, null, shape.getBBox());
  polygon.addContour(indices);
  polygon.triangulate();

  if (polygon.triangulationFailed) {
    throw new Error("Failed to triangulate sketched geometry");
  }

  let defaultModel = facility.getDefaultModel();

  let externalId = '';

  if (fuzzyEqual(height, 0.0, 0.01)) {
    console.log('Fixed zero height and set it to 1.0');
    height = 1.0;
  }

  const geom = polygon.toExtrudedMesh(height);
  if (geom) {
    // 'toExtrudedMesh()' above extrudes from maxZ=0 to minZ=-thickness
    // so here we add z value we got on mouse hit plus level height
    let res = await defaultModel.createGeometry(geom);
    res.transform[14] += elevation + height;

    const fragmentData = serializeFragment(0, res.hash, null, res.transform, true);
    const lmvFragment = base64EncArr(fragmentData, 0, FRAG_SIZE);

    let bbox = geom.boundingBox;
    bbox.min.z = elevation;
    bbox.max.z = elevation + height;

    const boundingBoxData = serializeBoundingBox(bbox);
    const lmvBBox = base64EncArr(boundingBoxData, 0, BBOX_SIZE);

    const elemData = {
      [QC.LmvDbId]: dbId,
      [QC.LmvFragment]: lmvFragment,
      [QC.LmvBoundingBox]: lmvBBox
    };

    heightChanged ? elemData[QC.Height] = height : elemData[QC.Elevation] = elevation;

    const rooms = defaultModel.getRooms();
    const room = rooms[dbId];
    if (room) {
      externalId = room.externalId;
    }

    let updatedRes = await defaultModel.updateElementsWithData([elemData], DtConstants.ChangeTypes.UpdateSpace, correlationId);
    updatedRes['externalId'] = externalId;

    return updatedRes;
  }

  return {
    numUpdated: 0
  };
}